import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Form, Formik } from "formik";

import { Autocomplete, Box, colors, Grid } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { colorsCupons } from "helpers/constants";

import { ICreateCupom, ICupomEvento } from "interfaces/cupom";
import { IEventoResponse } from "interfaces/eventos";

import Cupom from "layouts/pages/cadatros/cupons/components/Cupom";
import form from "layouts/pages/cadatros/cupons/schemas/forms";
import FormField from "layouts/pages/users/new-user/components/FormField";
import {
  initialValues,
  initialValuesEdit,
} from "layouts/pages/cadatros/cupons/schemas/initialValues";
import {
  validationsCreate,
  validationsEdit,
} from "layouts/pages/cadatros/cupons/schemas/validations";

import { editCupom, saveCupom } from "services/cupons";

import { setMessage, useMaterialUIController } from "context";

const { formId, formField } = form;
const {
  codigoPromocional,
  dataInicio,
  validade,
  qtdCupom,
  tipoPorcentagem,
  valorCupom,
  colorField,
} = formField;

interface Props {
  evento: IEventoResponse;
  cupom: ICupomEvento | null;
  updateListCupom: (cupom: ICupomEvento, action: "create" | "edit") => void;
}
export default function FormCupomEvento({
  evento,
  cupom,
  updateListCupom,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [colorCupom, setColorCupom] = useState<string | number>(
    cupom?.colorField || "#00A53C"
  );
  const cupomRef = useRef<HTMLDivElement>(null);
  const dataInitialValueEditCupom = cupom && initialValuesEdit(cupom);

  const isEditCupom = cupom ? true : false;
  const qtdCupomOld = cupom ? cupom.qtdCupom : 0;

  const baixarCupom = async () => {
    if (cupomRef.current === null) {
      return;
    }

    try {
      const dataUrl = await toPng(cupomRef.current, { cacheBust: true });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "cupom.png";
      link.click();
      link.remove();
    } catch (err) {
      console.error("Erro ao baixar o cupom:", err);
    }
  };

  const handleSubmit = async (params: any) => {
    const eventId =
      typeof params.evento === "object" && params.evento !== null && "id" in params.evento
        ? (params.evento as { id: number }).id
        : Number(params.evento);

    const dataCupom: ICreateCupom = {
      codigoPromocional: params.codigoPromocional,
      dataInicio: params.dataInicio,
      validade: params.validade,
      qtdCupom: Number(params.qtdCupom),
      disponivel: isEditCupom ? params.disponivel : Number(params.qtdCupom),
      tipoPorcentagem: params.tipoPorcentagem === "Sim",
      valorCupom: Number(params.valorCupom),
      colorField: params.color,
      eventId,
    };

    const action = isEditCupom
      ? editCupom(dataCupom, cupom.id, qtdCupomOld)
      : saveCupom(dataCupom);

    const messageText = isEditCupom
      ? "Atualizado dados do cupom"
      : "Criado novo cupom";

    await action
      .then(async (resp) => {
        if (!isEditCupom && resp.status !== 201) {
          setMessage(dispatch, {
            open: true,
            text: "Ops, algo deu errado",
            severity: "error",
          });
        } else {
          setMessage(dispatch, { open: true, text: messageText });
          updateListCupom(resp.data, isEditCupom ? "edit" : "create");
        }
      })
      .catch((err) => {
        setMessage(dispatch, {
          open: true,
          text: err.message,
          severity: "error",
        });
      });
  };

  return (
    <Formik
      initialValues={cupom ? dataInitialValueEditCupom : initialValues}
      validationSchema={cupom ? validationsEdit : validationsCreate}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({ values, errors, touched, handleChange, setFieldValue }) => {
        // Se o campo "evento" estiver vazio ou indefinido,
        // define o valor do campo "evento" com o id do objeto evento
        !values.evento && setFieldValue("evento", evento.id);
        // Se isEditCupom for verdadeiro e values.evento for uma string,
        // atualiza o valor do campo "evento" com o id do evento
        isEditCupom &&
          typeof values.evento === "string" &&
          setFieldValue("evento", evento.id);

        return (
          <Form id={formId}>
            <MDBox sx={{ mt: 3, width: "100%" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    type={codigoPromocional.type}
                    label={codigoPromocional.label}
                    name={codigoPromocional.name}
                    required
                    variant="outlined"
                    value={values.codigoPromocional}
                    onChange={handleChange}
                    onBlur={() => {
                      setFieldValue(
                        "codigoPromocional",
                        values.codigoPromocional
                          .toString()
                          .toUpperCase()
                          .replace(/\s+/g, "")
                          .trim()
                      );
                    }}
                    error={
                      errors.codigoPromocional && touched.codigoPromocional
                    }
                    success={
                      values.codigoPromocional.toString.length > 0 &&
                      !errors.codigoPromocional
                    }
                    sx={{ background: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormField
                    type={dataInicio.type}
                    label={dataInicio.label}
                    name={dataInicio.name}
                    required
                    variant="outlined"
                    onChange={handleChange}
                    value={values.dataInicio}
                    error={errors.dataInicio && touched.dataInicio}
                    success={
                      values.dataInicio.toString.length > 0 &&
                      !errors.dataInicio
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ background: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormField
                    type={validade.type}
                    label={validade.label}
                    name={validade.name}
                    required
                    variant="outlined"
                    onChange={handleChange}
                    value={values.validade}
                    error={errors.validade && touched.validade}
                    success={
                      values.validade.toString.length > 0 && !errors.validade
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ background: "white" }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} sm={3}>
                  <FormField
                    type={qtdCupom.type}
                    label={qtdCupom.label}
                    name={qtdCupom.name}
                    required
                    variant="outlined"
                    value={values.qtdCupom}
                    onChange={handleChange}
                    error={errors.qtdCupom && touched.qtdCupom}
                    success={
                      values.qtdCupom.toString.length > 0 && !errors.qtdCupom
                    }
                    sx={{ background: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormField
                    type={valorCupom.type}
                    label={valorCupom.label}
                    name={valorCupom.name}
                    required
                    variant="outlined"
                    value={values.valorCupom}
                    onChange={handleChange}
                    error={errors.valorCupom && touched.valorCupom}
                    success={
                      values.valorCupom.toString.length > 0 &&
                      !errors.valorCupom
                    }
                    sx={{ background: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    value={values.tipoPorcentagem}
                    options={["Sim", "Não"]}
                    onChange={(event, newValue) => {
                      handleChange({
                        target: {
                          name: tipoPorcentagem.name,
                          value: newValue,
                        },
                      });
                    }}
                    getOptionLabel={(option) => String(option)}
                    isOptionEqualToValue={(option, value) => option === value}
                    onBlur={handleChange}
                    renderInput={(params) => (
                      <FormField
                        {...params}
                        variant="outlined"
                        value={values.tipoPorcentagem}
                        name={tipoPorcentagem.name}
                        required
                        label={tipoPorcentagem.label}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          background: "white",
                          width: "100%",
                          height: "43px", // Ajustar a altura aqui para todos os componentes
                          "& .MuiOutlinedInput-root": {
                            padding: "10px", // Remove o padding interno para uniformizar
                            height: "43px", // Ajusta a altura interna do campo
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={colorsCupons}
                    value={values.color || null}
                    onChange={(_, newValue) => {
                      const colorValue = String(newValue ?? cupom?.colorField ?? "#00A53C");
                      setColorCupom(colorValue);
                      setFieldValue(colorField.name, colorValue);
                    }}
                    getOptionLabel={(option) => String(option)} // Exibe o código da cor como rótulo
                    isOptionEqualToValue={(option, value) => option === value}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        display="flex"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: String(option),
                            marginRight: 1,
                            borderRadius: "50%",
                          }}
                        />
                        {String(option)}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <FormField
                        {...params}
                        variant="outlined"
                        value={values.color}
                        name={colorField.name}
                        required
                        label={colorField.label}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <Box
                              sx={{
                                width: 15,
                                height: 15,
                                backgroundColor:
                                  values.color && String(values.color),
                                marginRight: 1,
                                borderRadius: "50%",
                              }}
                            />
                          ),
                        }}
                        sx={{
                          background: "white",
                          width: "100%",
                          height: "43px", // Ajustar a altura aqui para todos os componentes
                          "& .MuiOutlinedInput-root": {
                            padding: "10px", // Remove o padding interno para uniformizar
                            height: "43px", // Ajusta a altura interna do campo
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MDBox>

            {cupom && (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                sx={{
                  width: "100%",
                  border: `2px dashed ${colors.grey[300]}`,
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Cupom ref={cupomRef} data={cupom} color={colorCupom} />

                <MDButton
                  variant="outlined"
                  color="info"
                  sx={{ mt: 2 }}
                  onClick={baixarCupom}
                >
                  Baixar
                </MDButton>
              </MDBox>
            )}
            <MDBox display="flex" justifyContent="flex-end" sx={{ p: 2 }}>
              <MDButton variant="contained" color="light" type="submit">
                {cupom ? "Salvar alterações" : "Salvar"}
              </MDButton>
            </MDBox>
          </Form>
        );
      }}
    </Formik>
  );
}
